import React, { useState, useRef, useCallback, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface FloatingWindowProps {
    id: string;
    title: string;
    isOpen: boolean;
    onClose: () => void;
    initialPosition?: { x: number; y: number };
    initialSize?: { width: number; height: number };
    minWidth?: number;
    minHeight?: number;
    children: React.ReactNode;
}

interface Size {
    width: number;
    height: number;
}

const FloatingWindow: React.FC<FloatingWindowProps> = memo(({
    id,
    title,
    isOpen,
    onClose,
    initialPosition = { x: 100, y: 100 },
    initialSize = { width: 400, height: 300 },
    minWidth = 250,
    minHeight = 200,
    children
}) => {
    // Use refs for position to avoid re-renders during drag
    const windowRef = useRef<HTMLDivElement>(null);
    const positionRef = useRef(initialPosition);
    const sizeRef = useRef(initialSize);

    // Only use state for size (needed for ResponsiveContainer)
    const [size, setSize] = useState<Size>(initialSize);

    const isDraggingRef = useRef(false);
    const isResizingRef = useRef(false);
    const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

    // Apply initial position on mount
    useEffect(() => {
        if (windowRef.current && isOpen) {
            windowRef.current.style.transform = `translate(${positionRef.current.x}px, ${positionRef.current.y}px)`;
        }
    }, [isOpen]);

    // Drag handlers - direct DOM manipulation for performance
    const handleDragStart = useCallback((e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.floating-window-resize-handle')) return;
        e.preventDefault();
        isDraggingRef.current = true;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            posX: positionRef.current.x,
            posY: positionRef.current.y
        };
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
    }, []);

    // Use native event listeners for drag/resize to avoid React overhead
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDraggingRef.current && windowRef.current) {
                const deltaX = e.clientX - dragStartRef.current.x;
                const deltaY = e.clientY - dragStartRef.current.y;
                // Allow moving anywhere on viewport, clamp to keep at least 50px visible
                const newX = Math.max(-sizeRef.current.width + 50, Math.min(window.innerWidth - 50, dragStartRef.current.posX + deltaX));
                const newY = Math.max(0, Math.min(window.innerHeight - 50, dragStartRef.current.posY + deltaY));

                positionRef.current = { x: newX, y: newY };
                windowRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
            }

            if (isResizingRef.current && windowRef.current) {
                const deltaX = e.clientX - resizeStartRef.current.x;
                const deltaY = e.clientY - resizeStartRef.current.y;
                const newWidth = Math.max(minWidth, resizeStartRef.current.width + deltaX);
                const newHeight = Math.max(minHeight, resizeStartRef.current.height + deltaY);

                sizeRef.current = { width: newWidth, height: newHeight };
                windowRef.current.style.width = `${newWidth}px`;
                windowRef.current.style.height = `${newHeight}px`;
            }
        };

        const handleMouseUp = () => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
            if (isResizingRef.current) {
                isResizingRef.current = false;
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                // Update React state only on resize end (for ResponsiveContainer)
                setSize({ ...sizeRef.current });
            }
        };

        // Use passive: false to allow preventDefault
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [minWidth, minHeight]);

    // Resize handler
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        isResizingRef.current = true;
        resizeStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            width: sizeRef.current.width,
            height: sizeRef.current.height
        };
        document.body.style.cursor = 'nwse-resize';
        document.body.style.userSelect = 'none';
    }, []);

    if (!isOpen) return null;

    // Use portal to render at document.body level, escaping any container constraints
    return createPortal(
        <div
            ref={windowRef}
            style={{
                ...windowStyle,
                width: size.width,
                height: size.height,
                transform: `translate(${initialPosition.x}px, ${initialPosition.y}px)`,
            }}
        >
            {/* Header - Draggable */}
            <div
                style={headerStyle}
                onMouseDown={handleDragStart}
            >
                <span style={titleStyle}>{title}</span>
                <button
                    style={closeButtonStyle}
                    onClick={onClose}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    ×
                </button>
            </div>

            {/* Content */}
            <div style={contentStyle}>
                {children}
            </div>

            {/* Resize Handle */}
            <div
                className="floating-window-resize-handle"
                style={resizeHandleStyle}
                onMouseDown={handleResizeStart}
            >
                <svg width="10" height="10" viewBox="0 0 10 10">
                    <path d="M0 10 L10 0 M4 10 L10 4 M8 10 L10 8" stroke="#666" strokeWidth="1" />
                </svg>
            </div>
        </div>,
        document.body
    );
});

FloatingWindow.displayName = 'FloatingWindow';

// Styles - simplified for performance
const windowStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    backgroundColor: 'rgba(20, 20, 20, 0.98)', // Made more opaque since no blur
    border: '1px solid #4fc3f7',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 2000,
    // Removed backdropFilter and willChange for performance
};

const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: 'rgba(30, 30, 30, 0.9)',
    borderBottom: '1px solid #333',
    cursor: 'grab',
    userSelect: 'none',
};

const titleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#4fc3f7',
    fontFamily: 'monospace',
};

const closeButtonStyle: React.CSSProperties = {
    background: 'rgba(200, 60, 60, 0.8)',
    border: '1px solid #aa4444',
    color: '#fff',
    cursor: 'pointer',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: 1,
};

const contentStyle: React.CSSProperties = {
    flex: 1,
    padding: '12px',
    overflow: 'auto',
    minHeight: 0,
};

const resizeHandleStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '16px',
    height: '16px',
    cursor: 'nwse-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
};

export default FloatingWindow;
